import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // undefined = checking, null = not authenticated, object = authenticated user
  const [user, setUser] = useState(undefined);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const loginWithCredentials = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const registerClient = async (payload) => {
    // v1.36 double opt-in: /register/client returns
    // { status: "verification_sent", email, message } and NO cookies.
    // The Meta Pixel CompleteRegistration event fires ONLY on
    // /verifica-email success — better data quality on Meta, protects
    // our Brevo sender score from fake emails.
    const { data } = await api.post("/auth/register/client", payload);
    return data;
  };

  const registerClientFree = async (payload) => {
    const { data } = await api.post("/auth/register/free", payload);
    return data;
  };

  const registerAgency = async (payload) => {
    const { data } = await api.post("/auth/register/agency", payload);
    // Agency signup still fires the pixel: no double opt-in but there's
    // an admin-approval step which already filters bots.
    if (typeof window !== "undefined" && typeof window.fbq !== "undefined") {
      window.fbq("track", "CompleteRegistration");
    }
    return data;
  };

  const verifyEmail = async (token) => {
    // v1.36 — consume the verification token, log the user in, and fire
    // Meta Pixel CompleteRegistration ONLY on the first successful
    // verification (data.first_time). Second clicks on the same link
    // still log them in but don't double-count the conversion.
    const { data } = await api.post("/auth/verify-email", { token });
    setUser(data.user);
    if (
      data.first_time &&
      typeof window !== "undefined" &&
      typeof window.fbq !== "undefined"
    ) {
      window.fbq("track", "CompleteRegistration");
    }
    return data;
  };

  const resendVerification = async (email) => {
    const { data } = await api.post("/auth/resend-verification", { email });
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    setUser(null);
  };

  const value = {
    user,
    isLoading: user === undefined,
    isAuthenticated: !!user,
    fetchMe,
    refreshUser: fetchMe,
    loginWithCredentials,
    registerClient,
    registerClientFree,
    registerAgency,
    verifyEmail,
    resendVerification,
    logout,
    apiError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
